import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AnalyticsManager from 'app/analytics';
import { combineClassNames } from 'app/functions';
import HeaderExpendWindow from './components/header-expend-window';
import HeaderNavItem from './components/header-nav-item';
import { AnalyticsParamValues } from 'constants/analytics';
import {
    HOMEPAGE_NAV_ITEM_TAB,
    HOMEPAGE_NAV_ITEM_BUTTON,
    HOMEPAGE_NAV_ITEM_EXPEND_BUTTON,
    HOMEPAGE_DEFAULT_SLIDER_TAB,
    SPACE_CHECK_TIMEOUT,
} from 'constants/homepage-types';
import expendIconPath from 'assets/icons/arrow-down-black.svg';
import './index.scss';

const SliderHeader = ({
    id,
    title,
    iconPath,
    onTitleClick = () => {},
    showMoreButton = null,
    onShowMoreButtonClick = () => {},
    tabsList = [],
    buttonsList = [],
    selectedTab = HOMEPAGE_DEFAULT_SLIDER_TAB,
    onTabSelect = () => {},
    isLoading = false,
    analyticsType = null,
    hideMoreDealsButton = false,
    ...remainingProps
}) => {
    const translations = useSelector((store) => store.config.Translations);
    const deviceState = useSelector((store) => store.state.deviceState);

    const isMobileOrTablet = (deviceState === 'mobile' || deviceState === 'tablet');

    const [visibleTabs, setVisibleTabs] = useState([]);
    const [hiddenTabs, setHiddenTabs] = useState([]);
    const [isExpended, setIsExpended] = useState(false);
    const [isSpaceBeingChecked, setIsSpaceBeingChecked] = useState(false);

    const containerRef = useRef(null);
    const expendButtonRef = useRef(null);

    const analyticsData = [id, title, analyticsType];

    const isDefaultTabSelected = (selectedTab === HOMEPAGE_DEFAULT_SLIDER_TAB);
    const displayDefaultTabButton = (hiddenTabs.length > 0);
    const displayExpendButton = (isSpaceBeingChecked || (isMobileOrTablet && buttonsList.length > 0) || hiddenTabs.length > 0);

    const toggleExpend = (newValue = !isExpended) => setIsExpended(newValue);

    const handleTitleClick = () => {
        if (hideMoreDealsButton) {
            return;
        }
        if (isMobileOrTablet || isDefaultTabSelected) {
            AnalyticsManager.logHomepageSliderTitlePress(...analyticsData);
            onTitleClick();
            return;
        }

        // AnalyticsManager.logViewMainSliderTabHomePage
        AnalyticsManager.logHomepageSliderTabPress(...analyticsData, AnalyticsParamValues.DEFAULT_TAB);
        onTabSelect(HOMEPAGE_DEFAULT_SLIDER_TAB);
    };

    const handleNavItemSelect = (navItemData) => {
        switch (navItemData?.type) {
            case HOMEPAGE_NAV_ITEM_TAB:
                AnalyticsManager.logHomepageSliderTabPress(...analyticsData, navItemData.title);
                break;

            case HOMEPAGE_NAV_ITEM_BUTTON:
                AnalyticsManager.logHomepageSliderButtonPress(...analyticsData, navItemData.text);
                break;
        }
    };

    const handleNavItemSelectAndExpend = (navItemData) => {
        handleNavItemSelect(navItemData);
        toggleExpend();
    };

    const renderVisibleNavItem = (navItemData) => (
        <HeaderNavItem
            key={navItemData.key}
            data={navItemData}
            onSelect={handleNavItemSelect}
            selectedTab={selectedTab}
            onTabSelect={onTabSelect}
            isVisible={true}
        />
    );

    const getOverflowingWidth = () => {
        const containerWidth = containerRef?.current?.offsetWidth || 0;

        const containerChildren = Array.from(containerRef?.current?.children);
        const containerChildrenWidth = containerChildren.reduce((previousValue, currentValue) => {
            const currentStyles = getComputedStyle(currentValue);
            return previousValue
                + currentValue?.offsetWidth
                + parseInt(currentStyles?.marginLeft)
                + parseInt(currentStyles?.marginRight);
        }, 0);

        return (containerChildrenWidth - containerWidth);
    };

    const triggerSpaceCheckIfOverflowing = (forceCheck = false) => {
        if (tabsList.length > 0 && (forceCheck || getOverflowingWidth() > 0)) {
            setIsSpaceBeingChecked(true);
        }
    };

    const preformSpaceCheck = () => {
        if (
            tabsList.length === 0
            || !isSpaceBeingChecked
            || !containerRef.current
        ) return;

        const containerChildren = Array.from(containerRef.current.children);

        const visibleTabsNumber = visibleTabs.length;
        const hiddenTabsNumber = hiddenTabs.length;

        const expendButtonSpace = expendButtonRef?.current?.offsetWidth || 0;

        let currentTab = visibleTabsNumber,
            tabsToKeep = visibleTabsNumber,
            tabsToHide = hiddenTabsNumber,
            overflowingSpace = getOverflowingWidth();

        while (currentTab > 0) {
            const currentTabElement = containerChildren[currentTab];
            const currentTabStyles = getComputedStyle(currentTabElement);

            // Check if the header is overflowing without the "expand" button
            const ifNotReallyOverflowing = (
                currentTab === visibleTabsNumber && (overflowingSpace - expendButtonSpace) < 0
            );

            if (ifNotReallyOverflowing) {
                overflowingSpace = 0
            } else if (overflowingSpace > 0) {
                tabsToKeep--;
                tabsToHide++;
                overflowingSpace -= currentTabElement?.offsetWidth
                    + parseInt(currentTabStyles?.marginLeft)
                    + parseInt(currentTabStyles?.marginRight);
            }

            currentTab--;
        }

        setVisibleTabs([...tabsList].splice(0, tabsToKeep));
        setHiddenTabs([...tabsList].splice(-tabsToHide, tabsToHide));

        if (isSpaceBeingChecked) {
            setIsSpaceBeingChecked(false);
        }
    };

    useEffect(() => {
        window.addEventListener('resize', triggerSpaceCheckIfOverflowing);
        return () => window.removeEventListener('resize', triggerSpaceCheckIfOverflowing);
    }, []);

    useEffect(() => {
        if (isMobileOrTablet) {
            setVisibleTabs([]);
            setHiddenTabs(tabsList);
        } else {
            setVisibleTabs(tabsList);
            setHiddenTabs([]);

            triggerSpaceCheckIfOverflowing(true);
        }
    }, [tabsList, deviceState, containerRef]);

    useLayoutEffect(() => {
        // We use a small timeout to allow the DOM to fully load
        setTimeout(preformSpaceCheck, SPACE_CHECK_TIMEOUT);
    }, [isSpaceBeingChecked]);

    return (
        <div
            className={combineClassNames(
                'homepage-slider-header',
                isLoading && 'homepage-slider-header_loading',
                remainingProps.className,
            )}
            ref={containerRef}
        >
            <div className="header-title">
                <img src={iconPath} alt={title} className="header-title-icon" />

                <h2
                    className={combineClassNames(
                        'header-title-text',
                        isDefaultTabSelected && 'header-title-text_selected',
                    )}
                    onClick={handleTitleClick}
                >
                    {title}
                </h2>

                {isMobileOrTablet && displayExpendButton && (
                    <HeaderExpendWindow
                        navItemsList={[...hiddenTabs, ...buttonsList]}
                        isExpended={isExpended}
                        toggleExpend={handleNavItemSelectAndExpend}
                        selectedTab={selectedTab}
                        onTabSelect={onTabSelect}
                        includeDefaultTabButton={displayDefaultTabButton}
                    >
                        {renderVisibleNavItem({
                            key: 'expend-button',
                            onClick: toggleExpend,
                            iconPath: expendIconPath,
                            iconModifier: isExpended ? 'expend-icon-open' : 'expend-icon-close',
                            iconDir: 'left',
                            type: HOMEPAGE_NAV_ITEM_EXPEND_BUTTON,
                        })}
                    </HeaderExpendWindow>
                )}
            </div>

            {isMobileOrTablet ? (
                showMoreButton && (
                    <button
                        type="button"
                        className="header-show-more-button"
                        onClick={onShowMoreButtonClick}
                    >
                        {showMoreButton}
                    </button>
                )
            ) : (
                <>
                    {visibleTabs.length > 0 && visibleTabs.map(renderVisibleNavItem)}

                    {displayExpendButton && (
                        <HeaderExpendWindow
                            navItemsList={hiddenTabs}
                            isExpended={isExpended}
                            toggleExpend={handleNavItemSelectAndExpend}
                            selectedTab={selectedTab}
                            onTabSelect={onTabSelect}
                            ref={expendButtonRef}
                        >
                            {renderVisibleNavItem({
                                key: 'expend-button',
                                text: translations['category_slide_show_more_button'],
                                onClick: toggleExpend,
                                iconPath: expendIconPath,
                                iconModifier: isExpended ? 'expend-icon-open' : 'expend-icon-close',
                                iconDir: 'left',
                                type: HOMEPAGE_NAV_ITEM_EXPEND_BUTTON,
                            })}
                        </HeaderExpendWindow>
                    )}

                    {showMoreButton && renderVisibleNavItem({
                        key: 'show-more-button',
                        text: showMoreButton,
                        onClick: onShowMoreButtonClick,
                        type: HOMEPAGE_NAV_ITEM_BUTTON,
                    })}

                    {buttonsList.length > 0 && buttonsList.map(renderVisibleNavItem)}
                </>
            )}
        </div>
    );
};

export default SliderHeader;
